import {
  Alert,
  Box,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { chooseFirstSync, listUnlistedProducts } from '../../api/business/endpoints';
import type { FirstSyncDirection, IntegrationLink } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime, formatNumber } from '../../components/format';
import { useLanguage } from '../../i18n/LanguageContext';

type FirstSyncPanelProps = { link: IntegrationLink; canManage: boolean };

type ImportCount =
  | 'productsCreated'
  | 'variantsCreated'
  | 'batchesCreated'
  | 'filled'
  | 'receipts'
  | 'receiptQuantity'
  | 'stockKept'
  | 'skipped'
  | 'failed';

const DIRECTIONS: Array<FirstSyncDirection> = ['APP_TO_SHOP', 'SHOP_TO_APP'];

const IMPORT_COUNTS: Array<ImportCount> = [
  'productsCreated',
  'variantsCreated',
  'batchesCreated',
  'filled',
  'receipts',
  'receiptQuantity',
  'stockKept',
  'skipped',
  'failed',
];

/** How many of the products the shop does not have are named before "and N more". */
const UNLISTED_SHOWN: number = 8;

const Count = ({ label, value }: { label: string; value: string }) => (
  <Grid size={{ xs: 6, sm: 4, md: 2 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6">{value}</Typography>
  </Grid>
);

/**
 * The first sync of a connected shop (R0036): until the owner chooses, nothing syncs and the shop's orders are
 * held. Two choices, each saying what it overwrites, what it creates and what becomes of the held orders; a
 * confirmation; then the import's progress and what the choice did.
 */
export const FirstSyncPanel = ({ link, canManage }: FirstSyncPanelProps) => {
  const { language, t } = useLanguage();
  const [confirming, setConfirming] = useState<FirstSyncDirection | null>(null);
  const keys = [
    'integration-link',
    'integration-links',
    'integration-log',
    'integration-mappings',
    'integration-unlisted',
  ];
  const choose = useAppMutation(chooseFirstSync, keys, () => setConfirming(null));
  const unlisted = useQuery({
    queryKey: ['integration-unlisted', link.id],
    queryFn: () => listUnlistedProducts(link.companyId, link.id),
    enabled: link.status === 'ACTIVE' && link.firstSyncDirection !== 'SHOP_TO_APP',
  });

  if (link.status !== 'ACTIVE' && link.firstSyncDirection === null) {
    return null;
  }

  const unlistedCount: number = unlisted.data?.length ?? 0;
  const number = (value: number): string => formatNumber(value, language);

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {t('firstSync.title')}
      </Typography>
      <ErrorNotice error={unlisted.error} onRetry={() => void unlisted.refetch()} />
      {link.firstSyncState === 'AWAITING_CHOICE' ? (
        <>
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            {t('firstSync.awaiting')}
          </Alert>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 3 }} sx={{ mb: 2 }}>
            <Typography variant="body2">
              {t('firstSync.held', { count: number(link.heldChanges) })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('firstSync.matched', { mapped: number(link.mapped), unmatched: number(link.unmatched) })}
            </Typography>
          </Stack>
          <Grid container spacing={2}>
            {DIRECTIONS.map((direction: FirstSyncDirection) => {
              const offered: boolean = link.directionsOffered.includes(direction);

              return (
                <Grid key={direction} size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {t(`firstSync.${direction}` as const)}
                    </Typography>
                    {(['overwrites', 'creates', 'held'] as const).map((part) => (
                      <Box key={part}>
                        <Typography variant="caption" color="text.secondary">
                          {t(part === 'held' ? 'firstSync.heldOrders' : (`firstSync.${part}` as const))}
                        </Typography>
                        <Typography variant="body2">
                          {t(`firstSync.${direction}.${part}` as const, { count: number(unlistedCount) })}
                        </Typography>
                      </Box>
                    ))}
                    {!offered ? (
                      <Typography variant="body2" color="warning.main">
                        {t('firstSync.unsupported', { version: link.connectorVersion ?? '—' })}
                      </Typography>
                    ) : null}
                    <Box sx={{ flexGrow: 1 }} />
                    {canManage ? (
                      <Box>
                        <Button
                          variant={direction === 'APP_TO_SHOP' ? 'contained' : 'outlined'}
                          disabled={!offered || choose.isPending}
                          onClick={() => setConfirming(direction)}
                        >
                          {t('firstSync.choose')}
                        </Button>
                      </Box>
                    ) : null}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </>
      ) : null}
      {link.firstSyncState === 'IMPORTING' ? (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          {t('firstSync.importing', { items: number(link.lastImport?.items ?? 0) })}
          <LinearProgress sx={{ mt: 1.5 }} />
        </Alert>
      ) : null}
      {link.firstSyncDirection !== null && link.firstSyncChosenAt !== null ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('firstSync.done', {
            direction: t(`firstSync.${link.firstSyncDirection}` as const),
            who: link.firstSyncChosenByEmail ?? '—',
            when: formatDateTime(link.firstSyncChosenAt, language),
          })}
        </Typography>
      ) : null}
      {link.firstSyncDirection !== null ? (
        <Grid container spacing={2}>
          {link.firstSyncDirection === 'SHOP_TO_APP' && link.lastImport !== null
            ? IMPORT_COUNTS.map((key) => (
                <Count
                  key={key}
                  label={t(`firstSync.result.${key}` as const)}
                  value={number(link.lastImport?.[key] ?? 0)}
                />
              ))
            : null}
          {link.firstSyncState === 'DONE' ? (
            <>
              <Count label={t('firstSync.result.heldApplied')} value={number(link.firstSyncHeldApplied)} />
              {link.firstSyncDirection === 'SHOP_TO_APP' ? (
                <Count
                  label={t('firstSync.result.heldAbsorbed')}
                  value={number(link.firstSyncHeldAbsorbed)}
                />
              ) : null}
            </>
          ) : null}
        </Grid>
      ) : null}
      {link.firstSyncDirection !== 'SHOP_TO_APP' && unlistedCount > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">
            {t('firstSync.unlisted', { count: number(unlistedCount) })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('firstSync.unlistedHint')}
          </Typography>
          <List dense disablePadding>
            {(unlisted.data ?? []).slice(0, UNLISTED_SHOWN).map((product) => (
              <ListItem key={product.id} disableGutters>
                <ListItemText
                  primary={product.name}
                  secondary={[product.ean, product.sku].filter(Boolean).join(' · ')}
                />
              </ListItem>
            ))}
          </List>
          {unlistedCount > UNLISTED_SHOWN ? (
            <Typography variant="caption" color="text.secondary">
              {t('firstSync.unlistedMore', { count: number(unlistedCount - UNLISTED_SHOWN) })}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      {confirming !== null ? (
        <ConfirmDialog
          title={t('firstSync.confirmTitle', { direction: t(`firstSync.${confirming}` as const) })}
          message={t(`firstSync.confirm.${confirming}` as const, { count: number(link.heldChanges) })}
          confirmLabel={t(`firstSync.${confirming}` as const)}
          onConfirm={() =>
            choose.mutate({ companyId: link.companyId, linkId: link.id, direction: confirming })
          }
          onClose={() => setConfirming(null)}
          isBusy={choose.isPending}
          error={choose.error}
        />
      ) : null}
    </Paper>
  );
};
